// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Kaizen Dashboard components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// @mui material components
import TableRow from "@mui/material/TableRow";

// Kaizen Dashboard base styles
import borders from "assets/theme/base/borders";
import colors from "assets/theme/base/colors";
import { useEffect, useState } from "react";
import { makeNiceText } from "utils/formatters";
import { Icon, Tooltip } from "@mui/material";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import { formatMoneyPunto } from "utils/formatters";
import { computeDeliveryPercentage } from "utils/provisionQuantity";

const QUANTITY_EPSILON = 1e-6;
function TableItem({ item, handleUpdate, disabled, cambiarProducto, showDestinoColumn }) {
  const { light } = colors;
  const { borderWidth } = borders;

  const [itemData, setItemData] = useState({
    ...item,
    cantidad: item?.cantidad || 0,
  });

  const handleChanges = (e) => {
    const { name, value } = e.target;
    setItemData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  useEffect(() => {
    handleUpdate(itemData);
  }, [itemData]);

  // Sincroniza el estado interno cuando cambia el item de props (por ejemplo, al cambiar el producto)
  useEffect(() => {
    setItemData((prev) => ({
      ...prev,
      ...item,
      // Si el nuevo item trae cantidad definida, úsala; si no, conserva la actual
      cantidad: item?.cantidad ?? prev.cantidad ?? 0,
    }));
  }, [item?.idProvisionDetalle, item?.id, item?.idMateriaPrimaDestino,
    item?.coeficienteCompraConfirmado, item?.equivalenciaRequiereConfirmacion, item?.equivalenciaInvalida]);
  const deliveredMatchesRequired =
    (Number(item?.cantidadEntregada) || 0) + QUANTITY_EPSILON >=
    (Number(item?.cantidadRequerida) || 0);

  const deliveryPercentage = computeDeliveryPercentage({
    delivered: item?.cantidadEntregada,
    incoming: !disabled && itemData.enabled ? itemData?.cantidad : 0,
    required: item?.cantidadRequerida,
  });
  const isAlternative = item?.idMateriaPrimaReferencia != null;
  const hasDestino = Boolean(item?.idMateriaPrimaDestino);
  const showReplaceButton = !disabled && item.cantidadEntregada == 0 && isAlternative;

  return (
    <TableRow
      hover
      borderBottom={`${borderWidth[1]} solid ${light.main}`}
      opacity={itemData.enabled ? 1 : 0.5}
      sx={{ opacity: itemData.enabled ? 1 : 0.5 }}
    >
      <SoftBox component="td" data-label="ID" p={1} textAlign={"center"} sx={{ verticalAlign: "bottom" }}>
        <SoftTypography verticalAlign="text-top" variant="caption" color="dark" fontWeight="bold">
          {item?.id}
        </SoftTypography>
      </SoftBox>
      <SoftBox component="td" data-label="Nombre de compra" p={1} textAlign={"left"} sx={{ verticalAlign: "bottom" }}
      >
        <SoftTypography verticalAlign="text-top" variant="caption" color="secondary" fontWeight="medium"
        >
          {
            (
              item?.requierePack && item?.unidadPack
                ? makeNiceText(item?.nombreCompra)
                : makeNiceText(item?.nombre)
            )?.length > 70
              ? (
                item?.requierePack && item?.unidadPack
                  ? makeNiceText(item?.nombreCompra).slice(0, 70)
                  : makeNiceText(item?.nombre).slice(0, 70)
              ) + "..."
              : (
                item?.requierePack && item?.unidadPack
                  ? makeNiceText(item?.nombreCompra)
                  : makeNiceText(item?.nombre)
              )
          }

          {item?.idMateriaPrimaReferencia != null && (
            <SoftTypography variant="caption" color="textSecondary" fontWeight="bold" >
              {" (" + item.idMateriaPrimaReferencia + ")"}
            </SoftTypography>
          )}
          {item?.disabled && (
            <SoftTypography verticalAlign="text-top" variant="caption" color="error" fontWeight="medium" sx={{ cursor: "pointer" }}
            >
              &nbsp; (Deshabilitado)
            </SoftTypography>
          )}
        </SoftTypography>
        {item?.id !== item?.mpOriginal?.id && (
          <Tooltip title={(<>
            <h4>Materia prima reemplazada</h4>
            <h5>Original:</h5>
            <h6>{item?.mpOriginal?.id} - {item?.mpOriginal?.nombre}</h6>
          </>)} placement="top">
            <Icon fontSize="medium" sx={{ cursor: "pointer", ml: 1 }}>swap_vertical</Icon>
          </Tooltip>
        )}
      </SoftBox>
      {showDestinoColumn && (
        <SoftBox component="td" data-label="MP destino" p={1} textAlign={"left"} sx={{ verticalAlign: "middle" }}>
          <SoftBox display="flex" alignItems="center" gap={1} minHeight="34px">
            {isAlternative && !hasDestino ? (
              <SoftTypography variant="caption" color="error" fontWeight="bold">
                Pendiente
              </SoftTypography>
            ) : (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {(item?.materiaPrimaDestino?.id || item?.idMateriaPrimaDestino || item?.id)} -{" "}
                {makeNiceText(item?.materiaPrimaDestino?.nombre || item?.nombre)}
              </SoftTypography>
            )}
            {showReplaceButton && (
              <Tooltip title="Reemplazar materia prima" placement="top">
                <Icon fontSize="medium" sx={{ cursor: "pointer" }} onClick={() => cambiarProducto(item)}>
                  change_circle
                </Icon>
              </Tooltip>
            )}
          </SoftBox>
        </SoftBox>
      )}
      <SoftBox component="td" data-label="Rubro" p={1} textAlign={"left"} sx={{ verticalAlign: "bottom" }}>
        <SoftTypography verticalAlign="text-top" variant="caption" color="secondary" fontWeight="medium">
          {item?.categoria ? makeNiceText(item?.categoria) : "Sin rubro"}
        </SoftTypography>
      </SoftBox>

      <SoftBox component="td" data-label="Recibido / solicitado" p={1} textAlign={"center"} sx={{ verticalAlign: "middle" }}>
        <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="40px" gap={0.5}>
          <SoftTypography variant="caption" color="secondary" fontWeight="medium">
            {formatMoneyPunto(item?.cantidadEntregada ?? 0)}/{formatMoneyPunto(item?.cantidadRequerida ?? 0)}
          </SoftTypography>
          {deliveredMatchesRequired && (<Icon color="success" fontSize="small">done</Icon>)}
        </SoftBox>
      </SoftBox>

      <SoftBox component="td" data-label="Cantidad a ingresar" p={1} textAlign={"center"} sx={{ verticalAlign: "middle" }}>
        <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="40px">
          {disabled ? (
            <SoftTypography variant="caption" color="secondary" fontWeight="medium" >
              {formatMoneyPunto(item?.cantidad)}
            </SoftTypography>
          ) : (
            <SoftInputNumber
              placeholder="Cantidad"
              name="cantidad"
              value={itemData?.cantidad}
              onChange={handleChanges}
              error={
                itemData?.cantidad < 0
              }
            />
          )}
          <SoftTypography variant="caption" fontWeight="bold" sx={{ ml: 1 }}>
            {item?.requierePack ? item?.unidadPack : item?.tipoUnidad?.abreviatura || item?.tipoUnidad?.nombre}
          </SoftTypography>
        </SoftBox>
      </SoftBox>
      <SoftBox component="td" data-label="% acumulado al guardar" p={1} textAlign="center" sx={{ verticalAlign: "middle" }}>
        <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="40px">
          <SoftTypography variant="caption" color={deliveryPercentage > 100 ? "warning" : "secondary"} fontWeight="medium">
            {formatMoneyPunto(deliveryPercentage)}%
          </SoftTypography>
        </SoftBox>
      </SoftBox>
      <SoftBox component="td" data-label="Unidad" p={1} textAlign={"center"} sx={{ verticalAlign: "middle" }}>
        <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="40px">
          <SoftTypography variant="caption" color="secondary" fontWeight="medium" >
            {item?.requierePack && item?.unidadPack ? item?.unidadPack : item?.tipoUnidad?.nombre}
          </SoftTypography>
        </SoftBox>
      </SoftBox>
      {!disabled && (
        <SoftBox
          component="td" data-label="Selección"
          p={1}
          textAlign={"center"}
          sx={{ verticalAlign: "middle" }}
        >
          <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="40px" sx={{ cursor: "pointer" }} >
            <SoftBox display="flex" justifyContent="center" alignItems="center">
              <Tooltip title="Selección" placement="top">
                <Icon
                  sx={{
                    verticalAlign: "middle",
                    cursor: "pointer",
                  }}
                  fontSize="lg"
                  onClick={() => {
                    setItemData((prev) => {
                      if (prev.cantidadRequerida - prev.cantidadEntregada > 0) {
                        return {
                          ...prev,
                          enabled: !prev.enabled,
                        };
                      }
                      return prev;
                    });
                  }}
                >
                  {itemData.enabled ? "check_box" : "check_box_outline_blank"}
                </Icon>
              </Tooltip>
            </SoftBox>
          </SoftBox>
        </SoftBox>
      )}
    </TableRow>
  );
}

// Typechecking props of the TableItem
TableItem.propTypes = {
  item: PropTypes.object.isRequired,
  handleUpdate: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  cambiarProducto: PropTypes.func,
  showDestinoColumn: PropTypes.bool,
};

export default TableItem;

